'use client';

/**
 * QuickAddSheet — unified bottom drawer for adding items.
 *
 * Layout (top → bottom):
 *  1. List title input + autocomplete dropdown (new list OR existing)
 *  2. ItemSearchPanel (category chips + search + results) OR manual title input
 *  3. Pending queue               (shown once ≥ 1 item staged)
 *  4. Sticky CTA button
 *
 * All business logic lives in `useUnifiedAdd`; this file is purely
 * presentational. The sheet stays open during staging — close only happens
 * after a successful submit (the hook calls reset()).
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Plus, Check, Search, CheckCircle2, ListPlus } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUnifiedAdd, PendingItem } from '@/hooks/use-unified-add';
import { fetchCollections } from '@/lib/api';
import type { SearchResult } from '@/lib/api';
import { useDebounce } from '@/hooks/use-debounce';
import { CategoryEmoji } from '@/components/favely/category-emoji';
import { ItemSearchPanel, ProviderConf, ResolvedCategory } from './item-search-panel';

// ── Props ─────────────────────────────────────────────────────────────────────

interface QuickAddSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function QuickAddSheet({ open, onOpenChange }: QuickAddSheetProps) {
  const hook = useUnifiedAdd();

  // ── Local UI state ────────────────────────────────────────────────────────

  const [showDropdown, setShowDropdown] = useState(false);
  // Tracks how far the sheet must be pushed up to clear the software keyboard
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [vvHeight, setVvHeight] = useState<number | null>(null);
  const listInputRef = useRef<HTMLInputElement>(null);

  // When the visual viewport resizes (keyboard open/close), push the sheet up
  // so it sits above the keyboard rather than being obscured by it.
  const updateViewport = useCallback(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    // How much of the layout viewport is hidden below the visual viewport
    const layoutHeight = window.innerHeight;
    const hiddenBelow = layoutHeight - (vv.offsetTop + vv.height);
    setKeyboardOffset(Math.max(0, hiddenBelow));
    setVvHeight(vv.height);
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    vv.addEventListener('resize', updateViewport);
    vv.addEventListener('scroll', updateViewport);
    updateViewport();
    return () => {
      vv.removeEventListener('resize', updateViewport);
      vv.removeEventListener('scroll', updateViewport);
    };
  }, [updateViewport]);

  const debouncedListInput = useDebounce(hook.listInput, 200);

  // On open: restore draft if one exists (S3.8); otherwise start blank
  useEffect(() => {
    if (open) {
      hook.restoreDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close: if pending items exist save as draft; otherwise reset (S3.8)
  // Exception: if we just submitted (lastSubmitDestination is set), fully
  // reset instead of saving a draft — the items were already persisted (A3).
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      if (hook.lastSubmitDestination) {
        // Items were already saved — don't re-save as draft
        hook.reset();
      } else if (hook.pendingItems.length > 0) {
        // Preserve staged items for when the user reopens
        hook.saveDraft();
      } else {
        hook.reset();
      }
      setShowDropdown(false);
    }
    onOpenChange(nextOpen);
  };

  // ── Data fetching ─────────────────────────────────────────────────────────

  const { data: collectionsData } = useQuery({
    queryKey: ['collections', 'mine'],
    queryFn: () => fetchCollections({ mine: true }),
    enabled: open,
  });

  // ── Derived: collection autocomplete ──────────────────────────────────────

  const allCollections = (collectionsData?.collections ?? []).filter((c) => !c.isDefault);

  const filteredCollections = debouncedListInput.trim().length > 0
    ? allCollections.filter((c) =>
        c.title.toLowerCase().includes(debouncedListInput.toLowerCase())
      )
    : allCollections.slice(0, 6);

  const exactMatch = allCollections.find(
    (c) => c.title.toLowerCase() === hook.listInput.toLowerCase()
  );
  const showCreateNew = hook.listInput.trim().length > 0 && !exactMatch;

  // ── Handlers ──────────────────────────────────────────────────────────────

  /** Stage an enriched result from ItemSearchPanel. */
  function handleSelect(
    result: SearchResult,
    providerConf: ProviderConf | null,
    effectiveCategory: ResolvedCategory | null
  ) {
    if (!effectiveCategory) return;
    const item: PendingItem = {
      key: result.externalId,
      title: result.title,
      thumbnail: result.thumbnail,
      categoryId: effectiveCategory._id,
      categoryLabel: providerConf?.label ?? 'Item',
      searchResult: result,
      apiProvider: providerConf?.apiProvider ?? null,
      mediaType:
        providerConf?.value === 'tv' ? 'tv'
        : providerConf?.value === 'movie' ? 'movie'
        : undefined,
    };
    hook.addPendingItem(item);
  }

  function selectExistingCollection(c: { _id: string; title: string }) {
    hook.setTarget({ _id: c._id, title: c.title });
    hook.setListInput(c.title);
    setShowDropdown(false);
  }

  function selectNewCollection() {
    hook.setTarget({ _id: 'NEW', title: hook.listInput.trim() });
    setShowDropdown(false);
  }

  function clearTarget() {
    hook.setTarget(null);
    hook.setListInput('');
    setShowDropdown(false);
    listInputRef.current?.focus();
  }

  async function handleSubmit() {
    await hook.submit();
    // A3 — don't close the sheet; hook sets lastSubmitDestination and clears
    // the target so the user can immediately pick a second list.
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        className="h-auto rounded-t-2xl flex flex-col overflow-hidden pb-safe"
        style={{
          // Push sheet up by the keyboard height so it's always fully visible
          bottom: keyboardOffset,
          // Cap height to the visible area above the keyboard (97% breathing room)
          maxHeight: vvHeight ? `${Math.round(vvHeight * 0.97)}px` : '92dvh',
          // Smooth tracking when keyboard appears/disappears
          transition: keyboardOffset > 0
            ? 'bottom 0.25s ease, max-height 0.25s ease'
            : undefined,
        }}
      >
        {/* ── Header ─────────────────────────────────────────────────── */}
        <SheetHeader className="shrink-0 px-4 pt-4 pb-2">
          <SheetTitle>Add to list</SheetTitle>
          <SheetDescription className="sr-only">
            Search for items and save them to a list or your library.
          </SheetDescription>
        </SheetHeader>

        {/* ── Scrollable body ─────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-4 space-y-4 pb-4">

          {/* 1. List title input + autocomplete */}
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                ref={listInputRef}
                placeholder="List title, or leave empty to add to library"
                value={hook.listInput}
                className="pl-9 text-base"
                autoComplete="off"
                onChange={(e) => {
                  hook.setListInput(e.target.value);
                  if (hook.target && hook.target.title !== e.target.value) {
                    hook.setTarget(null);
                  }
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => {
                  setTimeout(() => setShowDropdown(false), 150);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') clearTarget();
                }}
              />
              {hook.listInput.length > 0 && (
                <button
                  type="button"
                  onClick={clearTarget}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear list input"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Autocomplete dropdown */}
            {showDropdown && (
              <div className="absolute z-50 top-full mt-1 w-full rounded-xl border bg-popover shadow-md overflow-hidden">
                {showCreateNew && (
                  <button
                    type="button"
                    onMouseDown={selectNewCollection}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-muted text-left"
                  >
                    <Plus className="h-4 w-4 text-primary shrink-0" />
                    <span>
                      Create <span className="font-medium">"{hook.listInput.trim()}"</span>
                    </span>
                  </button>
                )}
                {filteredCollections.map((c) => (
                  <button
                    key={c._id}
                    type="button"
                    onMouseDown={() => selectExistingCollection(c)}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm hover:bg-muted text-left"
                  >
                    <span className="truncate">{c.title}</span>
                    <span className="text-muted-foreground shrink-0 ml-2">
                      {(c as any).itemCount ?? ''} {(c as any).itemCount !== undefined ? 'items' : ''}
                    </span>
                  </button>
                ))}
                {filteredCollections.length === 0 && !showCreateNew && (
                  <p className="px-3 py-2.5 text-sm text-muted-foreground">
                    No lists yet. Type a title to create one.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* 2. Search panel (category chips + results) */}
          <ItemSearchPanel
            maxHeight="max-h-64"
            onSelect={handleSelect}
            renderRowSuffix={(result) =>
              hook.isPending(result.externalId)
                ? <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                : null
            }
            suppressHighlight={(result) => hook.isPending(result.externalId)}
          />

          {/* 3. Pending queue */}
          {hook.pendingItems.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Added ({hook.pendingItems.length})
              </p>
              <ul className="space-y-1.5">
                {hook.pendingItems.map((item) => (
                  <li
                    key={item.key}
                    className="flex items-center gap-2.5 rounded-xl border bg-muted/40 px-3 py-2"
                  >
                    {item.thumbnail ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={item.thumbnail}
                        alt=""
                        width={28}
                        height={28}
                        className="h-7 w-7 rounded object-cover shrink-0"
                      />
                    ) : (
                      <CategoryEmoji
                        category={item.categoryLabel}
                        size="sm"
                        className="shrink-0 text-muted-foreground"
                      />
                    )}
                    <p className="flex-1 text-sm font-medium truncate min-w-0">{item.title}</p>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {item.categoryLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => hook.removePendingItem(item.key)}
                      className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                      aria-label={`Remove ${item.title}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ── A3: Multi-list session banner — shown after first submit ─── */}
        {hook.lastSubmitDestination && (
          <div className="shrink-0 mx-4 mb-2 rounded-xl bg-primary/8 border border-primary/20 px-3 py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
              <p className="text-sm text-foreground truncate">
                Added to <strong>{hook.lastSubmitDestination}</strong>
              </p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              {/* Add same items to another list */}
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1 px-2 text-xs"
                onClick={() => {
                  hook.resetTarget();
                  listInputRef.current?.focus();
                }}
              >
                <ListPlus className="h-3 w-3" />
                Another list
              </Button>
              {/* Done — close the sheet and fully reset */}
              <Button
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  hook.reset();
                  onOpenChange(false);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        )}

        {/* ── Sticky CTA ──────────────────────────────────────────────── */}
        <div className="shrink-0 px-4 pb-4 pt-2 border-t bg-background">
          <Button
            className="w-full"
            size="lg"
            disabled={!hook.canSubmit || hook.isSubmitting}
            onClick={handleSubmit}
          >
            {hook.isSubmitting ? 'Saving…' : hook.ctaLabel}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

